import { ChangeDetectorRef, Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from '../auth.service';
import { AppEnvService } from './app-env.service';
import { UserDataService } from './user-data-service';
import { AutologoutConfirmDialogService } from './autologout-confirm-dialog.service';
import { Router } from '@angular/router';
import { SharedService } from './shared.service';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private timeoutDuration: number;        // in ms
  private warningDuration: number;        // in ms
  private timeoutId: any;                 // setTimeout ID for logout
  private warningTimeoutId: any;          // setTimeout ID for warning

  public onLogout: Subject<void> = new Subject<void>();
  public isAutoLogout = false;
  private isUserActive = false;
  showMapnotsavedCnfrmDialog: boolean = false;

  // Bound once so it can actually be removed later - the previous
  // `window.addEventListener('mousemove', () => this.resetTimer())` passed a new
  // closure on every start() call, so nothing could ever be un-registered and
  // every start() stacked another pair of listeners for the lifetime of the tab.
  private readonly boundResetTimer = () => this.resetTimer();
  private listenersAttached = false;

  // True while the sign-out warning is on screen. Activity (mousemove/keydown) must
  // not reset the timers during that minute: doing so cancelled the pending
  // auto-logout while leaving the dialog open (it has disableClose), then stacked a
  // second warning one full timeout later - whose auto-logout closed only itself,
  // leaving the first dialog orphaned over the login page. Only the dialog's own
  // buttons or its countdown may end the warning.
  private warningDialogOpen = false;

  // Keeps the server session alive while the user is active. The server only moves
  // its expiry forward on requests, never on mouse/keyboard activity. Pages without
  // a refinery heartbeat (e.g. Settings) make no requests while the user works, so
  // the server session expired before the warning appeared, and Resume's
  // refresh() then found it dead and logged out. Every KEEP_ALIVE_INTERVAL_MS, if
  // there was activity since the last keep-alive, one session/status request is
  // sent, so the server's expiry is always at or after the last activity + timeout.
  // The interval must be at most warningDuration: after an idle gap, that guarantees
  // the next keep-alive reaches the server while the session is still valid.
  private static readonly KEEP_ALIVE_INTERVAL_MS = 30000;
  private keepAliveIntervalId: any;
  private activitySinceKeepAlive = false;

  constructor(
    private authService: AuthService,
    private confirmDialogService: AutologoutConfirmDialogService,
    private ngZone: NgZone,
    private env: AppEnvService,
    private userDataService: UserDataService,
    private router: Router
  

  ) {
    this.initializeTimeouts();
    this.startWatching();
  }

  private initializeTimeouts() {
    const timeoutValue = this.userDataService.getUserSessionTimeoutData() ?? 15; // minutes
    this.timeoutDuration = timeoutValue * 60 * 1000; // ms
    //this.warningDuration = Number(this.env.warningDuration());
    // 1 minute, or half the timeout if that's shorter. A 1-minute org timeout with a
    // 1-minute warning made the warning delay zero, so the dialog reopened instantly
    // after every Resume.
    this.warningDuration = Math.min(60000, this.timeoutDuration / 2);
    console.log(`[SessionTimeoutService] Initialized with timeout: ${this.timeoutDuration}ms, warning: ${this.warningDuration}ms`);
  }

  private startWatching() {
    this.resetTimer();
    if (!this.listenersAttached) {
      window.addEventListener('mousemove', this.boundResetTimer);
      window.addEventListener('keydown', this.boundResetTimer);
      this.listenersAttached = true;
    }
    if (!this.keepAliveIntervalId) {
      // Outside Angular's zone so the idle ticks don't trigger change detection;
      // re-enters the zone only when a keep-alive is actually sent.
      this.ngZone.runOutsideAngular(() => {
        this.keepAliveIntervalId = setInterval(() => this.sendKeepAliveIfActive(), SessionTimeoutService.KEEP_ALIVE_INTERVAL_MS);
      });
    }
    this.isUserActive = true;
  }

  private sendKeepAliveIfActive() {
    if (!this.activitySinceKeepAlive || this.warningDialogOpen || !this.authService.getLoginStatus()) {
      return;
    }
    this.activitySinceKeepAlive = false;
    this.ngZone.run(() => void this.authService.keepAlive());
  }

  private resetTimer() {
    if (this.warningDialogOpen) {
      return;
    }
    this.activitySinceKeepAlive = true;
    this.ngZone.run(() => {
      clearTimeout(this.timeoutId);
      clearTimeout(this.warningTimeoutId);

      // The warning opens warningDuration BEFORE the timeout, so the auto-logout
      // lands exactly at the configured timeout, the same moment the server session
      // would expire. (It used to open AT the timeout, so the client ran one
      // warningDuration past the server session, and Resume could never succeed.)
      const warningDelay = Math.max(this.timeoutDuration - this.warningDuration, 0);
      this.timeoutId = setTimeout(() => this.showWarningBeforeLogout(), warningDelay);
      this.isUserActive = true;
    });
  }

  /*private showWarningBeforeLogout() {
    if (this.authService.getLoginStatus() && this.isUserActive) {
      const readableWarning = this.convertMillisecondsToReadableTime(this.warningDuration);
      const dialogRef = this.confirmDialogService.openConfirmDialog(
        `You will be signed out in ${readableWarning}. Would you like to save your current settings?`
      );

      this.warningTimeoutId = setTimeout(() => {
        dialogRef.close();
        this.logout();
      }, this.warningDuration);

      dialogRef.afterClosed().subscribe(result => {
        clearTimeout(this.warningTimeoutId); // Clear if dialog closes manually
        if (!result) {
          this.logout();
        }
      });
    }
  }*/


    private showWarningBeforeLogout() {
      if (this.authService.getLoginStatus() && this.isUserActive && !this.warningDialogOpen) {
        this.warningDialogOpen = true;
        const url = this.router.url;
        const readableWarning = this.convertMillisecondsToReadableTime(this.warningDuration);
    
        let customMessage = url.includes('/dataTransformConfig/')
          ? `The Transformer Configuration may not have been saved. You will be signed out in ${readableWarning}. Would you like to save your current settings?`
          : `You will be signed out in ${readableWarning}. Would you like to save your current settings?`;
    
        // Play alert sound
        const audio = new Audio('assets/alert.mp3');
        audio.load();
        audio.play().catch(err => console.error('Failed to play alert sound:', err));
    
        const dialogRef = this.confirmDialogService.openConfirmDialog(customMessage);
    
        let logoutTriggered = false;
    
        // Auto logout after warning duration
        this.warningTimeoutId = setTimeout(() => {
          if (!logoutTriggered) {
            logoutTriggered = true;
            console.log('Auto logout triggered after warning timeout');
            dialogRef.close();
            this.logout();
          }
        }, this.warningDuration);
    
        // Handle manual close
        dialogRef.afterClosed().subscribe(result => {
          clearTimeout(this.warningTimeoutId);
          this.warningDialogOpen = false;
          // Already logged out elsewhere (another tab, a 401) - AppComponent's
          // closeAll() closed this dialog with no result; don't log out twice.
          if (!logoutTriggered && this.authService.getLoginStatus()) {
            logoutTriggered = true;
            if (!result) {
              this.logout();
            } else {
              // Resume: restart idle tracking, and confirm with the server that the
              // session is actually still alive - it may have expired server-side
              // during an idle stretch with no API traffic. refresh() drives the
              // normal logged-out path if it has.
              this.resetTimer();
              void this.authService.refresh();
            }
          }
        });
      }
    }

  // Awaits the server-side revoke before announcing logout, so the redirect below
  // runs with getLoginStatus() already false. Without the await, the redirect ran
  // while still "logged in", and PendingChangesGuard opened its unsaved-changes
  // dialog mid-logout, which was then left over the login page.
  private async logout() {
    this.isAutoLogout = true;
    this.stop();
    await this.authService.logout();
    this.onLogout.next();
  }

  public start(): void {
    this.initializeTimeouts();
    this.startWatching();
  }

  // Stops idle tracking entirely: clears any pending timers and removes the
  // activity listeners. Called on every path that ends a session (explicit
  // sign-out, remote-tab logout, a 401) so a logged-out tab doesn't keep timing
  // toward a warning dialog that showWarningBeforeLogout() would just skip
  // anyway (it already no-ops once getLoginStatus() is false) - and so a
  // subsequent login's start() isn't attaching listeners on top of ones that
  // were never actually needed after logout.
  public stop(): void {
    clearTimeout(this.timeoutId);
    clearTimeout(this.warningTimeoutId);
    clearInterval(this.keepAliveIntervalId);
    this.keepAliveIntervalId = null;
    this.activitySinceKeepAlive = false;
    if (this.listenersAttached) {
      window.removeEventListener('mousemove', this.boundResetTimer);
      window.removeEventListener('keydown', this.boundResetTimer);
      this.listenersAttached = false;
    }
    this.isUserActive = false;
  }

  public async manualLogout() {
    this.isAutoLogout = false;
    this.stop();
    await this.authService.logout();
    this.onLogout.next();
  }

  private convertMillisecondsToReadableTime(duration: number): string {
    if (duration >= 3600000) {
      const hours = duration / 3600000;
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (duration >= 60000) {
      const minutes = duration / 60000;
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      const seconds = duration / 1000;
      return `${seconds} second${seconds > 1 ? 's' : ''}`;
    }
  }
}
